import React from 'react'
import { components } from 'react-select'
import { useDebounce } from 'hooks/useDebounce'

import { sort, unique } from 'utils/array'
import PlaceholderOption from 'common/PlaceholderOption'
import ReactSelect from 'common/Forms/ReactSelect'
import ShowMoreButton from 'common/ShowMoreButton'

// All this lookup logic is only temporary until we implement a proper search category/sub category APIs in BE
export default ({
  index,
  resource,
  showSubCategories,
  categories: categoriesProp,
  category: categoryProp,
  subCategory: subCategoryProp,
  disabled,
  updateCart,
}) => {
  const [category, setCategory] = React.useState(categoryProp)
  const [subCategory, setSubCategory] = React.useState(subCategoryProp)
  const [isDisableSubCategory, setIsDisableSubCategory] = React.useState(showSubCategories)
  const [filteredBaseCategories, setFilteredBaseCategories] = React.useState([])
  const [filteredBaseSubCategories, setFilteredBaseSubCategories] = React.useState([])

  // Category pagination state
  const [categoryInputValue, setCategoryInputValue] = React.useState('')
  const [categoryOptions, setCategoryOptions] = React.useState([])
  const [categoryPage, setCategoryPage] = React.useState(1)
  const [categoryLoadingMore, setCategoryLoadingMore] = React.useState(false)
  const [categoryMenuIsOpen, setCategoryMenuIsOpen] = React.useState(undefined)
  const [categoryHasSelected, setCategoryHasSelected] = React.useState(false)
  const categoryPageSize = 50

  // SubCategory pagination state
  const [subCategoryInputValue, setSubCategoryInputValue] = React.useState('')
  const [subCategoryOptions, setSubCategoryOptions] = React.useState([])
  const [subCategoryPage, setSubCategoryPage] = React.useState(1)
  const [subCategoryLoadingMore, setSubCategoryLoadingMore] = React.useState(false)
  const [subCategoryMenuIsOpen, setSubCategoryMenuIsOpen] = React.useState(undefined)
  const [subCategoryHasSelected, setSubCategoryHasSelected] = React.useState(false)
  const subCategoryPageSize = 50

  const originalCategories = React.useMemo(() => {
    if (categoriesProp) {
      const uniqueItems = unique(categoriesProp, 'name')
      return uniqueItems
    }
    return []
  }, [categoriesProp])

  // Load category options with pagination
  const loadCategoryOptions = React.useCallback(
    async (search, page) => {
      setCategoryLoadingMore(true)
      
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // Use filtered base categories (which may be filtered by subcategory)
      let filteredCategories = [...filteredBaseCategories]
      
      if (search && search.trim()) {
        filteredCategories = filteredCategories.filter(cat => 
          cat.name.toLowerCase().includes(search.toLowerCase())
        )
      }
      
      // Simulate pagination
      const startIndex = (page - 1) * categoryPageSize
      const endIndex = startIndex + categoryPageSize
      const paginatedData = filteredCategories.slice(startIndex, endIndex)
      
      setCategoryLoadingMore(false)

      if (page === 1) {
        setCategoryOptions(paginatedData)
      } else {
        setCategoryOptions(prev => [...prev, ...paginatedData])
      }

      // Only focus if there's a search term or menu is open (user is actively interacting)
      if ((search && search.trim()) || categoryMenuIsOpen) {
        const input = document.querySelector(`[id="items.${index}.category"] input`)
        if (input) {
          input.focus()
        }
      }
    },
    [filteredBaseCategories, categoryPageSize, index, categoryMenuIsOpen]
  )

  // Load subcategory options with pagination
  const loadSubCategoryOptions = React.useCallback(
    async (search, page) => {
      setSubCategoryLoadingMore(true)
      
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // Use filtered base subcategories (which may be filtered by category)
      let filteredSubCategories = [...filteredBaseSubCategories]
      if (search && search.trim()) {
        filteredSubCategories = filteredSubCategories.filter(subCat => 
          subCat.name.toLowerCase().includes(search.toLowerCase())
        )
      }
      
      // Simulate pagination
      const startIndex = (page - 1) * subCategoryPageSize
      const endIndex = startIndex + subCategoryPageSize
      const paginatedData = filteredSubCategories.slice(startIndex, endIndex)
      
      setSubCategoryLoadingMore(false)

      if (page === 1) {
        setSubCategoryOptions(paginatedData)
      } else {
        setSubCategoryOptions(prev => [...prev, ...paginatedData])
      }

      // Only focus if there's a search term or menu is open (user is actively interacting)
      if ((search && search.trim()) || subCategoryMenuIsOpen) {
        const input = document.querySelector(`[id="items.${index}.subCategory"] input`)
        if (input) {
          input.focus()
        }
      }
    },
    [filteredBaseSubCategories, subCategoryPageSize, index, subCategoryMenuIsOpen]
  )

  React.useEffect(() => {
    if (originalCategories?.length) {
      setFilteredBaseCategories([...originalCategories])
      
      // Initialize subcategories based on selected category
      if (categoryProp) {
        const newSubCategories = getSubCategories(categoryProp)
        if (newSubCategories.length) {
          setIsDisableSubCategory(false)
          setFilteredBaseSubCategories(newSubCategories)
        } else {
          setIsDisableSubCategory(true)
          setFilteredBaseSubCategories([])
        }
      } else {
        // No category selected - keep subcategory disabled
        setIsDisableSubCategory(true)
        setFilteredBaseSubCategories([])
      }
    }
  }, [originalCategories, categoryProp])

  // Load categories when filtered base categories or related state changes
  React.useEffect(() => {
    if (filteredBaseCategories.length > 0) {
      loadCategoryOptions(categoryInputValue, categoryPage)
    }
  }, [categoryInputValue, categoryPage, loadCategoryOptions])

  // Load subcategories when filtered base subcategories or related state changes
  React.useEffect(() => {
    if (filteredBaseSubCategories.length > 0) {
      loadSubCategoryOptions(subCategoryInputValue, subCategoryPage)
    }
  }, [subCategoryInputValue, subCategoryPage, loadSubCategoryOptions])

  const debouncedSetCategoryInputValue = useDebounce(value => {
    setCategoryInputValue(value)
    setCategoryPage(1)
    setCategoryHasSelected(false)
  }, 300)

  const debouncedSetSubCategoryInputValue = useDebounce(value => {
    setSubCategoryInputValue(value)
    setSubCategoryPage(1)
    setSubCategoryHasSelected(false)
  }, 300)

  const handleCategoryInputChange = (newValue, actionMeta) => {
    // Only handle user input, not other events
    if (actionMeta.action === 'input-change') {
      debouncedSetCategoryInputValue(newValue)
      return newValue
    }
    return newValue
  }

  const handleSubCategoryInputChange = (newValue, actionMeta) => {
    // Only handle user input, not other events
    if (actionMeta.action === 'input-change') {
      debouncedSetSubCategoryInputValue(newValue)
      return newValue
    }
    return newValue
  }

  const getCategories = selectedSubCategory => {
    let items
    if (selectedSubCategory) {
      items = categoriesProp.filter(item => item.subcategory === selectedSubCategory.name)
    } else {
      items = categoriesProp
    }

    const uniqueItems = unique(items, 'name')
    return uniqueItems
  }

  const getSubCategories = selectedCategory => {
    const items = categoriesProp
      .filter(item => (!selectedCategory || item.name === selectedCategory.name) && !!item.subcategory)
      .map((item, index) => ({ id: index + 1, name: item.subcategory }))

    const uniqueItems = unique(items, 'name')
    const sortedItems = sort(uniqueItems, 'name')
    return sortedItems
  }

  const handleCategoryChange = selected => {
    // Clear the input value after selecting an option
    setCategoryInputValue('')
    setCategoryPage(1)
    setCategoryHasSelected(true)

    if (selected) {
      const newSubCategories = getSubCategories(selected)
      if (newSubCategories.length) {
        setIsDisableSubCategory(false)
        setFilteredBaseSubCategories(newSubCategories)
      } else {
        setIsDisableSubCategory(true)
        setSubCategory(null)
        setFilteredBaseSubCategories([])
      }
      // Reset subcategory pagination
      setSubCategoryPage(1)
      setSubCategoryInputValue('')
    } else {
      setFilteredBaseCategories([...originalCategories])
      const allSubCategories = getSubCategories()
      setFilteredBaseSubCategories(allSubCategories)
      setIsDisableSubCategory(false)
      setSubCategory(null)
      // Reset subcategory pagination
      setSubCategoryPage(1)
      setSubCategoryInputValue('')
    }
    setCategory(selected)
  }

  const handleCategoryMenuOpen = () => {
    setCategoryMenuIsOpen(true)
  }

  const handleCategoryMenuClose = () => {
    setCategoryMenuIsOpen(false)
  }

  const handleCategoryBlur = () => {
    // When input loses focus and no selection was made, load default list
    const hasSearchTerm = categoryInputValue && categoryInputValue.trim().length > 0

    if (hasSearchTerm && !categoryHasSelected) {
      // Load default list without search term
      setCategoryInputValue('')
      setCategoryPage(1)
      setCategoryHasSelected(false)
    }
  }

  const handleSubCategoryChange = selected => {
    // Clear the input value after selecting an option
    setSubCategoryInputValue('')
    setSubCategoryPage(1)
    setSubCategoryHasSelected(true)

    if (selected) {
      const newCategories = getCategories(selected)
      setFilteredBaseCategories(newCategories)
      // Reset category pagination
      setCategoryPage(1)
      setCategoryInputValue('')
    } else {
      setFilteredBaseCategories([...originalCategories])
      const allSubCategories = getSubCategories()
      setFilteredBaseSubCategories(allSubCategories)
      // Reset category pagination
      setCategoryPage(1)
      setCategoryInputValue('')
    }
    setSubCategory(selected)
  }

  const handleSubCategoryMenuOpen = () => {
    setSubCategoryMenuIsOpen(true)
  }

  const handleSubCategoryMenuClose = () => {
    setSubCategoryMenuIsOpen(false)
  }

  const handleSubCategoryBlur = () => {
    // When input loses focus and no selection was made, load default list
    const hasSearchTerm = subCategoryInputValue && subCategoryInputValue.trim().length > 0

    if (hasSearchTerm && !subCategoryHasSelected) {
      // Load default list without search term
      setSubCategoryInputValue('')
      setSubCategoryPage(1)
      setSubCategoryHasSelected(false)
    }
  }

  const categoryOptionsWithLoadMore = React.useMemo(() => {
    const result = Array.isArray(categoryOptions) ? [...categoryOptions] : []
    const totalLoaded = result.length
    
    // Check if there are more items to load based on filtered base categories
    let totalAvailable = filteredBaseCategories.length
    
    if (categoryInputValue && categoryInputValue.trim()) {
      totalAvailable = filteredBaseCategories.filter(cat => 
        cat.name.toLowerCase().includes(categoryInputValue.toLowerCase())
      ).length
    }
    
    const hasMore = totalLoaded > 0 && totalLoaded < totalAvailable && totalLoaded === categoryPageSize * categoryPage

    if (hasMore) {
      result.push({ isLoadMore: true, label: resource.ShowMore })
    }

    return result
  }, [categoryOptions, categoryPage, categoryPageSize, resource, filteredBaseCategories, categoryInputValue])

  const subCategoryOptionsWithLoadMore = React.useMemo(() => {
    const result = Array.isArray(subCategoryOptions) ? [...subCategoryOptions] : []
    const totalLoaded = result.length
    
    // Check if there are more items to load based on filtered base subcategories
    let totalAvailable = filteredBaseSubCategories.length
    if (subCategoryInputValue && subCategoryInputValue.trim()) {
      totalAvailable = filteredBaseSubCategories.filter(subCat => 
        subCat.name.toLowerCase().includes(subCategoryInputValue.toLowerCase())
      ).length
    }
    
    const hasMore = totalLoaded > 0 && totalLoaded < totalAvailable && totalLoaded === subCategoryPageSize * subCategoryPage

    if (hasMore) {
      result.push({ isLoadMore: true, label: resource.ShowMore })
    }

    return result
  }, [subCategoryOptions, subCategoryPage, subCategoryPageSize, resource, filteredBaseSubCategories, subCategoryInputValue])

  const requireSubCategory = !isDisableSubCategory && !!category

  return (
    <React.Fragment>
      <ReactSelect
        id={`items.${index}.category`}
        name={`items.${index}.category`}
        rules={{ required: !category && resource.Required }}
        value={category}
        label={resource.Category}
        placeholder={resource.SelectCategory}
        options={categoryOptionsWithLoadMore}
        optionValueKey="code"
        optionLabelKey="name"
        isClearable
        isSearchable={true}
        isDisabled={disabled}
        formGroup
        formClassName="w-100"
        labelClassName="d-xl-none mb-1"
        onInputChange={handleCategoryInputChange}
        onChange={selected => {
          handleCategoryChange(selected)
          updateCart()
        }}
        onMenuOpen={handleCategoryMenuOpen}
        onMenuClose={handleCategoryMenuClose}
        onBlur={handleCategoryBlur}
        menuIsOpen={categoryMenuIsOpen}
        isLoading={categoryLoadingMore}
        filterOption={null}
        components={{
          Option: ({ data, innerRef, ...innerProps }) => {
            if (data.isPlaceholder) {
              return <PlaceholderOption ref={innerRef} label={data.label} />
            }

            if (data.isLoadMore) {
              return (
                <ShowMoreButton
                  resource={resource}
                  disabled={categoryLoadingMore}
                  innerProps={innerProps}
                  setPage={setCategoryPage}
                />
              )
            }

            return (
              <components.Option data={data} {...innerProps}>
                {data.name}
              </components.Option>
            )
          },
        }}
      />
      {showSubCategories && (
        <ReactSelect
          id={`items.${index}.subCategory`}
          name={`items.${index}.subCategory`}
          rules={{ required: requireSubCategory && resource.Required }}
          value={!isDisableSubCategory ? subCategory : null}
          label={resource.SubCategory}
          placeholder={resource.SelectSubCategory}
          options={subCategoryOptionsWithLoadMore}
          optionValueKey="id"
          optionLabelKey="name"
          isClearable
          isSearchable={true}
          isDisabled={disabled || isDisableSubCategory}
          formGroup
          formClassName="w-100"
          labelClassName="d-xl-none mb-1"
          onInputChange={handleSubCategoryInputChange}
          onChange={selected => {
            handleSubCategoryChange(selected)
            updateCart()
          }}
          onMenuOpen={handleSubCategoryMenuOpen}
          onMenuClose={handleSubCategoryMenuClose}
          onBlur={handleSubCategoryBlur}
          menuIsOpen={subCategoryMenuIsOpen}
          isLoading={subCategoryLoadingMore}
          filterOption={null}
          components={{
            Option: ({ data, innerRef, ...innerProps }) => {
              if (data.isPlaceholder) {
                return <PlaceholderOption ref={innerRef} label={data.label} />
              }

              if (data.isLoadMore) {
                return (
                  <ShowMoreButton
                    resource={resource}
                    disabled={subCategoryLoadingMore}
                    innerProps={innerProps}
                    setPage={setSubCategoryPage}
                  />
                )
              }

              return (
                <components.Option data={data} {...innerProps}>
                  {data.name}
                </components.Option>
              )
            },
          }}
        />
      )}
    </React.Fragment>
  )
}

